import { useEffect, useId, useMemo, useState } from "react";
import { Button, DropdownSelect, QuantityStepper, Spinner, Typography } from "@/components/atoms";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { transferRateKind } from "@/redux/slices/clientBooking/bookingPayload";
import {
  setBookedForDuration,
  setBookingExtras,
  setBookingTransferType,
  setBookingVehicle,
  setFixedPrice,
  setJobRate,
  setNumOfBags,
  setNumOfPassengers,
} from "@/redux/slices/clientBooking/clientBookingSlice";
import { ClientBookingExtra } from "@/redux/slices/clientBooking/types";
import {
  selectBookedForDuration,
  selectBookingExtras,
  selectBookingTransferType,
  selectBookingVehicle,
  selectEditJobId,
  selectNumOfBags,
  selectNumOfPassengers,
} from "@/redux/slices/clientBooking/selectors";
import { formatCurrency } from "@/utils";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import { useBookingData } from "./hooks/useBookingData";
import { useForceValidate } from "./useForceValidate";
import { AddExtrasModal } from "./AddExtrasModal";
import { extrasLabel, filterVehiclesByTransfer } from "./vehicleTransfer";

type Layout = "card" | "sheet";

// Required selections — extras are optional. Transfer type has no default (user must choose); the
// vehicle auto-defaults to the first available, so it's only flagged when nothing is selectable.
// Passengers/bags start valid but can be typed out of range; their helper text is their error, so they
// aren't listed here. See validate() below.
type FieldErrors = Partial<Record<"transferTypeId" | "vehicleId" | "duration", string>>;

interface VehicleTransferPanelProps {
  /** "card" = desktop side panel; "sheet" = mobile bottom sheet (adds a Close action). */
  layout: Layout;
  onClose: () => void;
  /**
   * "card" layout only: called after Proceed commits the selection so the wizard opens the next Trip
   * Details sub-step (Other Details). The "sheet" layout closes back to the list instead.
   */
  onAdvance?: () => void;
  /** When true, show the required-field errors immediately (the user was routed here by a failed exit). */
  forceValidate?: boolean;
}

/** A form section: label above its control. */
const Field = ({ label, children }: React.PropsWithChildren<{ label: string }>) => (
  <div className="flex flex-col gap-2">
    <Typography variant="action" className="block text-neutral-dark-gray">
      {label}
    </Typography>
    {children}
  </div>
);

/** A count as a row: label and its capacity hint on the left, the stepper on the right. */
const CountField = ({
  label,
  noun,
  min,
  max,
  value,
  onChange,
  invalid,
  disabled,
}: {
  label: string;
  /** Singular, for the buttons: "Add another passenger". */
  noun: string;
  min: number;
  max: number;
  value: number | null;
  onChange: (value: number | null) => void;
  invalid: boolean;
  disabled: boolean;
}) => {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex flex-col gap-1">
        <label htmlFor={id}>
          <Typography variant="action" className="text-neutral-dark-gray">
            {label}
          </Typography>
        </label>
        <Typography id={`${id}-hint`} variant="paragraph" className={invalid ? "text-danger" : "text-neutral-silver-gray"}>
          {`Up to ${max} for this vehicle`}
        </Typography>
      </div>
      <QuantityStepper
        editable
        id={id}
        aria-describedby={`${id}-hint`}
        className="flex-shrink-0"
        label={noun}
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        invalid={invalid}
        disabled={disabled}
      />
    </div>
  );
};

/**
 * "Vehicle and Transfer" — the Vehicle sub-panel of the Trip Details step. Reads the bookable
 * transfer types / vehicles / extras from GET /v1/cust/data. Selecting a transfer type filters the
 * vehicle list by its `vehicle_exclusions` (legacy-sheet parity). Passenger + bag counts range over
 * the fleet's largest capacities so the capacity warning is reachable when the chosen vehicle is too
 * small. The selection is committed to clientBookingSlice on Proceed (not live), matching Figma.
 */
export const VehicleTransferPanel = ({ layout, onClose, onAdvance, forceValidate = false }: VehicleTransferPanelProps) => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  // Edit mode relabels the commit CTA "Confirm" (it closes the sub-panel rather than advancing).
  const isEdit = useAppSelector(selectEditJobId) != null;
  // Scope-aware: authed client data (/v1/cust/data) or guest job-data, normalised to one shape.
  const { data, isLoading } = useBookingData();

  const committedVehicle = useAppSelector(selectBookingVehicle);
  const committedTransfer = useAppSelector(selectBookingTransferType);
  const committedPassengers = useAppSelector(selectNumOfPassengers);
  const committedBags = useAppSelector(selectNumOfBags);
  const committedExtras = useAppSelector(selectBookingExtras);
  const committedDuration = useAppSelector(selectBookedForDuration);

  // Local draft, seeded from the committed selection; committed on Proceed.
  const [transferTypeId, setTransferTypeId] = useState<string | undefined>(committedTransfer ? String(committedTransfer.id) : undefined);
  const [vehicleId, setVehicleId] = useState<string | undefined>(committedVehicle ? String(committedVehicle.vehicle_id) : undefined);
  const [selectedExtras, setSelectedExtras] = useState<ClientBookingExtra[]>(committedExtras ?? []);
  const [extrasOpen, setExtrasOpen] = useState(false);
  // `null` while a count's box is cleared mid-typing.
  const [passengers, setPassengers] = useState<number | null>(committedPassengers ?? 1);
  const [bags, setBags] = useState<number | null>(committedBags ?? 0);
  // Duration in minutes, only used by hourly/daily transfer types.
  const [duration, setDuration] = useState<number | undefined>(committedDuration || undefined);

  const transferTypes = useMemo(() => {
    const listed = data?.transfer_types ?? [];
    // A replayed booking's own type when the list no longer offers it — deactivated, deleted or admin-only
    // (hydrated from the detail by mapBookingDetailToViewDraft). Listed so the dropdown SHOWS the type the
    // booking was made on and Confirm keeps it; choosing another entry is the only thing that changes it.
    // Replay only: a fresh draft never carries a type the list did not offer.
    const keepCommitted = (isEdit || viewOnly) && committedTransfer && !listed.some((t) => String(t.id) === String(committedTransfer.id));
    if (!keepCommitted) return listed;
    return [
      {
        id: committedTransfer.id,
        label: committedTransfer.label ?? "",
        type: committedTransfer.type ?? "",
        special_event: committedTransfer.special_event ?? false,
        vehicle_exclusions: committedTransfer.vehicle_exclusions ?? [],
      },
      ...listed,
    ];
  }, [data, isEdit, viewOnly, committedTransfer]);
  const vehicles = useMemo(() => data?.vehicles ?? [], [data]);
  const extras = data?.extras ?? [];

  // `/v1/cust/data` can return ids as numbers; normalise to string everywhere so the DropdownSelect
  // (and our lookups) compare like-for-like.
  const selectedTransfer = useMemo(() => transferTypes.find((t) => String(t.id) === transferTypeId), [transferTypes, transferTypeId]);

  // The chosen transfer type's billing shape. Hourly/daily require a duration; journey doesn't.
  const rateKind = transferRateKind(selectedTransfer?.type);
  const durationOptions = useMemo(
    () => (rateKind === "hourly" ? data?.hourly_rates ?? [] : rateKind === "daily" ? data?.daily_rates ?? [] : []),
    [rateKind, data]
  );

  // Keep the duration valid for the chosen rate: clear it for journey types; for hourly/daily default
  // to the first preset (mobile parity) and drop a value the new option set no longer offers.
  useEffect(() => {
    if (!data) return;
    if (rateKind === "journey") {
      setDuration(undefined);
      return;
    }
    setDuration((current) => {
      if (current && durationOptions.some((o) => o.value === current)) return current;
      return durationOptions[0]?.value;
    });
  }, [data, rateKind, durationOptions]);
  const availableVehicles = useMemo(() => filterVehiclesByTransfer(vehicles, selectedTransfer), [vehicles, selectedTransfer]);
  const selectedVehicle = useMemo(() => availableVehicles.find((v) => String(v.vehicle_id) === vehicleId), [availableVehicles, vehicleId]);

  // The count ranges come from the selected vehicle (mirrors the old sheet, which builds passengers
  // from the vehicle's seat count and rebuilds on vehicle change).
  // The count ranges come from the selected vehicle (mirrors the old sheet, which builds passengers
  // from the vehicle's seat count and rebuilds on vehicle change).
  const maxPassengers = selectedVehicle?.max_passengers ?? 1;
  const maxBags = selectedVehicle?.max_bags ?? 0;

  // Typed counts aren't clamped (only − / + are), so a count outside the vehicle's capacity, or a box
  // left empty, is flagged as it happens and blocks Proceed. Capacity is only judged against a vehicle
  // actually selected — the 1 / 0 fallbacks above would otherwise flag a replayed booking's counts.
  const passengersInvalid = passengers === null || passengers < 1 || (!!selectedVehicle && passengers > maxPassengers);
  const bagsInvalid = bags === null || (!!selectedVehicle && bags > maxBags);

  // No default transfer type — the client must choose one (it's a required field).

  // Keep the vehicle valid for the chosen transfer type: default to the first available, and drop a
  // selection the transfer type now excludes.
  useEffect(() => {
    if (!data) return;
    const stillValid = vehicleId && availableVehicles.some((v) => String(v.vehicle_id) === vehicleId);
    if (!stillValid) setVehicleId(availableVehicles.length ? String(availableVehicles[0].vehicle_id) : undefined);
  }, [data, availableVehicles, vehicleId]);

  // Clamp the counts into the selected vehicle's capacity whenever the vehicle changes — the old
  // sheet caps a too-high passenger count down to the new vehicle's seats.
  useEffect(() => {
    if (!selectedVehicle) return;
    setPassengers((p) => (p === null ? p : Math.min(Math.max(p, 1), selectedVehicle.max_passengers)));
    setBags((b) => (b === null ? b : Math.min(Math.max(b, 0), selectedVehicle.max_bags)));
  }, [selectedVehicle]);

  const [errors, setErrors] = useState<FieldErrors>({});

  // The chosen extras, named and priced. View-only reads the hydrated selection straight off the
  // draft so it still shows an extra the operator has since retired; editing reads the same list,
  // which the modal replaces wholesale on confirm.
  const chosenExtras = viewOnly ? committedExtras ?? [] : selectedExtras;
  const extrasCount = chosenExtras.reduce((total, extra) => total + (extra.quantity ?? 1), 0);
  const extrasTotal = chosenExtras.reduce((sum, extra) => sum + (extra.amount ?? 0) * (extra.quantity ?? 1), 0);

  // Validate the required selections and surface a per-field error for anything missing. Returns
  // whether the selection is complete, so both Proceed and forceValidate can share it.
  const validate = (): boolean => {
    // Transfer type is genuinely user-required — it has no default.
    const transferMissing = !transferTypeId;
    // Vehicle auto-defaults to the first available (see the effect above), so it's only really
    // missing when the loaded data + chosen transfer leave nothing selectable. Gating on `data` +
    // `availableVehicles` (not the raw `vehicleId`) also avoids a false "Please select a vehicle"
    // during load, or on the render before the default effect has applied.
    const vehicleMissing = !vehicleId && !!data && availableVehicles.length === 0;
    // Hourly/daily transfer types can't be quoted/booked without a duration.
    const durationMissing = rateKind !== "journey" && !duration;
    setErrors({
      transferTypeId: transferMissing ? "Please select a transfer type" : undefined,
      vehicleId: vehicleMissing ? "Please select a vehicle" : undefined,
      duration: durationMissing ? "Please select a duration" : undefined,
    });
    return !transferMissing && !vehicleMissing && !durationMissing && !passengersInvalid && !bagsInvalid;
  };
  // Routed here by a failed "Next Step": show the missing-selection errors right away.
  useForceValidate(forceValidate, validate);

  const handleProceed = () => {
    if (!validate()) return;
    if (!selectedVehicle || !selectedTransfer || passengers === null || bags === null) return;
    dispatch(
      setBookingVehicle({
        vehicle_id: selectedVehicle.vehicle_id,
        uuid: selectedVehicle.uuid,
        name: selectedVehicle.name,
        max_passengers: selectedVehicle.max_passengers,
        max_bags: selectedVehicle.max_bags,
      })
    );
    dispatch(setBookingTransferType({ id: Number(selectedTransfer.id), label: selectedTransfer.label, type: selectedTransfer.type }));
    // Rate + duration follow the transfer type (mobile: job_rate = transfer_type.type). Journey types
    // carry no duration; hourly/daily send the chosen minutes.
    dispatch(setJobRate(selectedTransfer.type));
    dispatch(setBookedForDuration(rateKind === "journey" ? 0 : duration));
    // The price override (set in the Price Summary) is keyed to the priced trip. Changing the transfer
    // type reprices the base, so drop any prior override — it would otherwise ride onto a different trip.
    if (Number(selectedTransfer.id) !== committedTransfer?.id) dispatch(setFixedPrice(undefined));
    dispatch(setNumOfPassengers(passengers));
    dispatch(setNumOfBags(bags));
    dispatch(setBookingExtras(selectedExtras));
    if (layout === "sheet") onClose();
    else onAdvance?.();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ViewOnlyFieldset>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
        <Typography variant="h2">Vehicle and Transfer</Typography>
        <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
          Select vehicle and transfer type
        </Typography>

        {isLoading ? (
          <div className="mt-10 flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            <Field label="Select Transfer Type">
              <DropdownSelect
                // Remount on value change so the (defaultValue-seeded) Listbox never keeps a stale
                // internal selection after a programmatic change.
                key={`transfer-${transferTypeId ?? ""}`}
                className="w-full"
                size="md"
                disabled={viewOnly}
                placeholder="Select Transfer Type"
                hasError={!!errors.transferTypeId}
                options={transferTypes.map((t) => ({ name: t.label, value: String(t.id) }))}
                value={transferTypeId}
                onChange={(value: string) => {
                  setTransferTypeId(value);
                  setErrors((e) => ({ ...e, transferTypeId: undefined }));
                }}
              />
              {errors.transferTypeId && (
                <Typography variant="paragraph" className="block text-danger">
                  {errors.transferTypeId}
                </Typography>
              )}
            </Field>

            {rateKind !== "journey" && (
              <Field label="Trip Duration">
                <DropdownSelect
                  key={`duration-${duration ?? ""}`}
                  className="w-full"
                  size="md"
                  disabled={viewOnly}
                  placeholder="Select Duration"
                  hasError={!!errors.duration}
                  options={durationOptions.map((o) => ({ name: o.label, value: String(o.value) }))}
                  value={duration != null ? String(duration) : undefined}
                  onChange={(value: string) => {
                    setDuration(Number(value));
                    setErrors((e) => ({ ...e, duration: undefined }));
                  }}
                />
                {errors.duration && (
                  <Typography variant="paragraph" className="block text-danger">
                    {errors.duration}
                  </Typography>
                )}
              </Field>
            )}

            <Field label="Select Vehicle">
              <DropdownSelect
                key={`vehicle-${vehicleId ?? ""}`}
                className="w-full"
                size="md"
                disabled={viewOnly}
                placeholder="Select Vehicle"
                hasError={!!errors.vehicleId}
                options={availableVehicles.map((v) => ({ name: v.name, value: String(v.vehicle_id) }))}
                value={vehicleId}
                onChange={(value: string) => {
                  setVehicleId(value);
                  setErrors((e) => ({ ...e, vehicleId: undefined }));
                }}
              />
              {errors.vehicleId && (
                <Typography variant="paragraph" className="block text-danger">
                  {errors.vehicleId}
                </Typography>
              )}
            </Field>

            {extras.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <Typography variant="action" className="block text-neutral-dark-gray">
                    Extras
                  </Typography>
                  <Typography variant="action" className="text-neutral-silver-gray">
                    {`${extrasCount} added`}
                  </Typography>
                </div>
                <div className="mt-0.5 border-b border-neutral-mid-gray" />

                {chosenExtras.length === 0 ? (
                  // Nothing chosen yet: the only affordance is the one that opens the picker.
                  !viewOnly && (
                    // `custom` is the atom's transparent variant — no fill, no border — which is the
                    // text-button the design asks for. Padding is dropped so it lines up with the
                    // label above it rather than sitting inset.
                    <Button variant="custom" size="sm" startIcon="Add" className="!px-0 self-start text-info" onClick={() => setExtrasOpen(true)}>
                      Add Extras
                    </Button>
                  )
                ) : (
                  <div className="flex items-center gap-3 rounded-lg border border-neutral-gray bg-neutral-surface-gray py-3 pl-4 pr-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center">
                        <Typography variant="action">{`${extrasCount} item${extrasCount === 1 ? "" : "s"}`}</Typography>
                        <span className="mx-2 h-1.5 w-1.5 rounded-full bg-neutral-mid-gray" />
                        <Typography variant="paragraph">{formatCurrency(extrasTotal, 2, "AUD")}</Typography>
                      </div>
                      {/* One line, truncated — the modal is where the full list lives. */}
                      <Typography variant="paragraph" className="mt-1 block truncate text-neutral-dark-gray">
                        {extrasLabel(chosenExtras)}
                      </Typography>
                    </div>
                    {!viewOnly && (
                      <button type="button" className="flex-shrink-0" onClick={() => setExtrasOpen(true)}>
                        <Typography variant="action" className="text-info">
                          Edit
                        </Typography>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <CountField
              label="Passengers"
              noun="passenger"
              min={1}
              max={maxPassengers}
              value={passengers}
              onChange={setPassengers}
              invalid={passengersInvalid}
              disabled={viewOnly}
            />

            <CountField
              label="Number of Bags"
              noun="bag"
              min={0}
              max={maxBags}
              value={bags}
              onChange={setBags}
              invalid={bagsInvalid}
              disabled={viewOnly}
            />
          </div>
        )}
      </div>
      </ViewOnlyFieldset>

      {/* Outside ViewOnlyFieldset: that fieldset is `disabled` in view-only mode and would make every
          control inside the modal inert. It only opens from buttons that view-only never renders. */}
      <AddExtrasModal
        open={extrasOpen}
        options={extras}
        selected={selectedExtras}
        onClose={() => setExtrasOpen(false)}
        onConfirm={(chosen) => {
          setSelectedExtras(chosen);
          setExtrasOpen(false);
        }}
      />

      {!viewOnly && (
        <div className="flex flex-col gap-2 px-5 pb-5">
          <Button variant="primary" size="lg" className="w-full" onClick={handleProceed}>
            {isEdit ? "Confirm" : "Proceed"}
          </Button>
          {layout === "sheet" && (
            <Button variant="custom" size="md" className="text-info" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
